/**
 * Inline SVG icon set (no emoji — per brand + a11y rules).
 * All icons share a 24x24 viewBox and inherit `currentColor`, so size/colour
 * are controlled by Tailwind classes on the wrapping element.
 */
import type { SVGProps } from 'react';

export type IconName =
  | 'trophy'
  | 'gift'
  | 'handshake'
  | 'voucher'
  | 'storefront'
  | 'megaphone'
  | 'link'
  | 'creator'
  | 'qr'
  | 'leads'
  | 'traffic'
  | 'chart'
  | 'video'
  | 'sparkles'
  | 'shield'
  | 'check'
  | 'arrow-right'
  | 'menu'
  | 'close'
  | 'car'
  | 'cart'
  | 'download'
  | 'clock'
  | 'alert';

type Props = SVGProps<SVGSVGElement> & {
  name: IconName;
  title?: string;
};

// Each entry is the inner markup of a 24x24, stroke-based icon.
const PATHS: Record<IconName, JSX.Element> = {
  trophy: (
    <>
      <path d="M8 21h8M12 17v4M7 4h10v5a5 5 0 0 1-10 0V4Z" />
      <path d="M17 5h3v2a3 3 0 0 1-3 3M7 5H4v2a3 3 0 0 0 3 3" />
    </>
  ),
  gift: (
    <>
      <rect x="3" y="8" width="18" height="4" rx="1" />
      <path d="M12 8v13M5 12v8a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-8" />
      <path d="M12 8S10 3 7.5 4.5 12 8 12 8Zm0 0s2-5 4.5-3.5S12 8 12 8Z" />
    </>
  ),
  handshake: (
    <path d="m11 17 2 2a1 1 0 0 0 1.4 0l4.3-4.3a1 1 0 0 0 0-1.4l-3-3M7 13l-3-3a1 1 0 0 1 0-1.4l4.3-4.3a1 1 0 0 1 1.4 0l1.3 1.3M3 12l4 4M16 7l-3 3-2-2M9 11l2 2" />
  ),
  voucher: (
    <>
      <path d="M3 8a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v2a2 2 0 0 0 0 4v2a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-2a2 2 0 0 0 0-4V8Z" />
      <path d="M14 6v12" strokeDasharray="2 2" />
    </>
  ),
  storefront: (
    <>
      <path d="M3 9 4.5 4.5A1 1 0 0 1 5.4 4h13.2a1 1 0 0 1 .9.5L21 9" />
      <path d="M3 9v0a3 3 0 0 0 6 0 3 3 0 0 0 6 0 3 3 0 0 0 6 0M5 11v9h14v-9M9 20v-5h6v5" />
    </>
  ),
  car: (
    <>
      <path d="M5 11l1.5-4.5A2 2 0 0 1 8.4 5h7.2a2 2 0 0 1 1.9 1.5L19 11" />
      <path d="M3 16v-3a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v3a1 1 0 0 1-1 1h-1M4 17H3" />
      <circle cx="7.5" cy="16.5" r="1.5" />
      <circle cx="16.5" cy="16.5" r="1.5" />
    </>
  ),
  cart: (
    <>
      <circle cx="9" cy="20" r="1" />
      <circle cx="18" cy="20" r="1" />
      <path d="M3 4h2l2.4 12.2a1 1 0 0 0 1 .8h8.7a1 1 0 0 0 1-.8L21 8H6" />
    </>
  ),
  megaphone: (
    <path d="M3 11v2a1 1 0 0 0 1 1h2l3 4V6L6 10H4a1 1 0 0 0-1 1Zm6-1 9-5v14l-9-5M18 8a3 3 0 0 1 0 8" />
  ),
  link: (
    <path d="M10 13a5 5 0 0 0 7 0l2-2a5 5 0 0 0-7-7l-1 1M14 11a5 5 0 0 0-7 0l-2 2a5 5 0 0 0 7 7l1-1" />
  ),
  creator: (
    <>
      <circle cx="12" cy="8" r="4" />
      <path d="M5 21a7 7 0 0 1 14 0" />
      <path d="M18 3l.8 1.6L20.5 5l-1.7.4L18 7l-.8-1.6L15.5 5l1.7-.4L18 3Z" />
    </>
  ),
  qr: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <path d="M14 14h3v3M21 14v7M17 21h4M14 18v3" />
    </>
  ),
  leads: (
    <>
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M19 8v6M16 11h6" />
    </>
  ),
  traffic: (
    <path d="M3 17l5-5 4 4 8-8M21 8h-4M21 8v4" />
  ),
  chart: (
    <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
  ),
  video: (
    <>
      <rect x="2" y="6" width="14" height="12" rx="2" />
      <path d="m16 10 6-3v10l-6-3" />
    </>
  ),
  sparkles: (
    <path d="M12 3l1.6 4.4L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.6L12 3ZM19 14l.8 2.2L22 17l-2.2.8L19 20l-.8-2.2L16 17l2.2-.8L19 14ZM5 14l.6 1.6L7 16l-1.4.4L5 18l-.6-1.6L3 16l1.4-.4L5 14Z" />
  ),
  shield: (
    <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3ZM9 12l2 2 4-4" />
  ),
  check: (
    <path d="M5 13l4 4L19 7" />
  ),
  'arrow-right': (
    <path d="M5 12h14M13 6l6 6-6 6" />
  ),
  menu: (
    <path d="M4 6h16M4 12h16M4 18h16" />
  ),
  close: (
    <path d="M6 6l12 12M18 6 6 18" />
  ),
  download: (
    <path d="M12 3v12M7 10l5 5 5-5M5 21h14" />
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </>
  ),
  alert: (
    <>
      <path d="M10.3 4.3 2.6 18a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z" />
      <path d="M12 9v4M12 17h.01" />
    </>
  ),
};

export function Icon({ name, title, className, ...rest }: Props) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      width="24"
      height="24"
      className={className}
      role={title ? 'img' : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      {PATHS[name]}
    </svg>
  );
}

export default Icon;
